/*
 * The mockup's creation hero: `.card.prompt` (the box shows the focus of the field inside it),
 * `.prompt-bar` (clip, name, scope chip, keys, submit) and `.suggest` (chips that fill the text).
 */
.srOnly {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.prompt {
  padding: var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--surface-raised);
  transition:
    border-color var(--motion-duration) var(--motion-ease),
    box-shadow var(--motion-duration) var(--motion-ease);
}

/* The measured 2px ring (D-UX-5) plus the mockup's soft halo, on the box around the field. */
.prompt:has(textarea:focus-visible) {
  outline: 2px solid var(--focus-ring);
  outline-offset: -1px;
  box-shadow: 0 0 0 3px var(--action-soft);
}

.prompt textarea {
  min-block-size: 84px;
  font-size: var(--font-size-16);
  line-height: var(--line-height-body);
}

.file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 var(--space-2) var(--space-2);
  color: var(--text-muted);
  font-size: var(--font-size-13);
}

.bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding-block-start: var(--space-1);
}

.attach {
  display: inline-grid;
  place-items: center;
  inline-size: var(--control-height);
  block-size: var(--control-height);
  border-radius: var(--radius-control);
  color: var(--text-muted);
  cursor: pointer;
}

.attach:hover {
  background: var(--state-hover);
  color: var(--text);
}

.attach:focus-within {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

/* The Lienzo's hero puts the name on its own line, first (`.hero-lienzo .prompt-bar .input`). */
.name {
  flex: 1 1 100%;
  order: -1;
}

.chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  block-size: var(--control-height);
  padding: 0 10px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-pill);
  background: var(--surface-raised);
  color: var(--text-muted);
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-medium);
}

.chip:focus-within {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

/* The native select fills the chip, so the whole chip opens it; its own arrow is the chip's chevron. */
.chipSelect {
  max-inline-size: 180px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  appearance: none;
  cursor: pointer;
  outline: none;
}

.spacer {
  flex: 1;
}

.keys {
  color: var(--text-subtle);
  font-size: var(--font-size-12);
}

.help {
  margin: var(--space-2) var(--space-2) 0;
  color: var(--text-subtle);
  font-size: var(--font-size-12);
}

.error {
  margin: var(--space-2) var(--space-2) 0;
  color: var(--danger-text);
  font-size: var(--font-size-12);
}

.suggest {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-block-start: var(--space-3);
}

.suggestion {
  display: inline-flex;
  align-items: center;
  block-size: var(--control-height);
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  background: var(--surface-raised);
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
}

.suggestion:hover {
  background: var(--state-hover);
  color: var(--text);
}

.suggestion:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

@media (max-width: 1023.98px) {
  .keys {
    display: none;
  }
}

/* One seat only: the label of the chip, with no select under it. */
.chipStatic {
  max-inline-size: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A button only to be reachable by keyboard (its hint shows on focus); it opens nothing. */
.chipReadOnly {
  cursor: default;
  font: inherit;
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-medium);
}
